import { CURRENCY } from "../../../../components/Constants";
import {
  ErrorMessage,
  Field,
  FormikErrors,
  FormikHelpers,
  FormikValues,
} from "formik";
import ReactSelect from "react-select";
import { ContainerSize } from "../../../../components/Enums";
import { n } from "../../sales-invoice/helpers/SalesInvoiceHelperFunction";
import { useEffect } from "react";

interface ProductFieldsProps {
  item: any;
  index: number;
  productList: any;
  unitList: any;
  packingType: any;
  values: FormikValues;
  remove: (index: number) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<FormikValues>>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  setAddUnitModalStatus: (show: boolean) => void;
  setAddPackingTypeModalStatus: (show: boolean) => void;
  setAddProductModalStatus: (show: boolean) => void;
  handleChangeProduct: (
    updatedProductIds: string[],
    values: any,
    setFieldValue: any,
    oldProductId: any,
    changedIndex?: number,
  ) => void;
  productWiseUsedQty?: {
    [product_id: string]: number;
  };
  setFieldError?: FormikHelpers<any>["setFieldError"];
  errors: any;
  permissions: any;
}

const ProductFields: React.FC<ProductFieldsProps> = ({
  item,
  index,
  remove,
  productList,
  unitList,
  packingType,
  values,
  setFieldValue,
  handleChange,
  setAddUnitModalStatus,
  setAddPackingTypeModalStatus,
  setAddProductModalStatus,
  handleChangeProduct,
  productWiseUsedQty,
  setFieldError,
  errors,
  permissions,
}) => {
  const round3 = (num: number) => Number(num.toFixed(3));
  const currentProductValue = values.sales_contract_products[index];

  const currentProduct = productList.find(
    (p: any) => p.id === currentProductValue.product_id,
  );
  const currentParentId =
    currentProduct?.parent_product_id || currentProduct?.id;

  const firstIndexOfParent = values.sales_contract_products.findIndex(
    (p: any) => {
      const prod = productList.find((x: any) => x.id === p.product_id);
      return (prod?.parent_product_id || prod?.id) === currentParentId;
    },
  );

  const parentProduct =
    firstIndexOfParent !== -1
      ? values.sales_contract_products[firstIndexOfParent]
      : null;

  const isFirstOfParent = index === firstIndexOfParent;

  const recalculateAllSiblings = () => {
    if (!isFirstOfParent) return;

    const parentPrice = n(currentProductValue.price);
    const parentPackingTypeId = currentProductValue.packing_type_id;
    const parentPackingNetWeight = n(currentProductValue.packing_net_weight);

    const packingWeight =
      packingType.find((p: any) => p.id === parentPackingTypeId)?.weight || 0;

    const packingGrossWeight = round3(
      parentPackingNetWeight + n(packingWeight),
    );

    const updatedProducts = [...values.sales_contract_products];

    updatedProducts.forEach((product: any, idx: number) => {
      const prod = productList.find((x: any) => x.id === product.product_id);

      const prodParentId = prod?.parent_product_id || prod?.id;

      if (prodParentId === currentParentId) {
        const quantity = n(product.quantity);

        const noOfBags = n(product.no_of_bags);

        const totalAmount = round3(quantity * parentPrice);

        const totalGrossWeight = round3(noOfBags * packingGrossWeight);
        const totalNetWeight = round3(noOfBags * parentPackingNetWeight);

        updatedProducts[idx] = {
          ...updatedProducts[idx],
          price: parentPrice,
          total_amount: totalAmount,
          packing_gross_weight: packingGrossWeight,
          total_gross_weight: totalGrossWeight,
          total_net_weight: totalNetWeight,
        };

        setFieldValue(`sales_contract_products.${idx}.price`, parentPrice);
        setFieldValue(
          `sales_contract_products.${idx}.total_amount`,
          totalAmount,
        );
        setFieldValue(
          `sales_contract_products.${idx}.packing_gross_weight`,
          packingGrossWeight,
        );
        setFieldValue(
          `sales_contract_products.${idx}.total_gross_weight`,
          totalGrossWeight,
        );
        setFieldValue(
          `sales_contract_products.${idx}.total_net_weight`,
          totalNetWeight,
        );
      }
    });

    calculateTotal(updatedProducts);
  };

  const updateValue = (key: string, val: any) => {
    const formatted = val && val > 0 ? +n(val).toFixed(3) : "";
    setFieldValue(key, formatted);

    return formatted;
  };

  const calculateTotal = (products: any[]) => {
    const totals = {
      amount: 0,
      bags: 0,
      net: 0,
      gross: 0,
    };

    for (const p of products) {
      totals.amount = round3(totals.amount + n(p.total_amount));
      totals.bags += n(p.no_of_bags);
      totals.net = round3(totals.net + n(p.total_net_weight));
      totals.gross = round3(totals.gross + n(p.total_gross_weight));
    }

    updateValue("sub_total", totals.amount);
    updateValue("total_no_of_bags", totals.bags);
    updateValue("total_net_weight", totals.net);
    updateValue("total_gross_weight", totals.gross);
  };

  const calculateProductAmount = () => {
    if (!parentProduct) return;
    const basePrice = isFirstOfParent
      ? n(currentProductValue.price)
      : n(parentProduct.price);

    const quantity = n(currentProductValue.quantity);

    const totalAmount = round3(quantity * basePrice);

    const updatedProducts = [...values.sales_contract_products];

    updatedProducts[index] = {
      ...updatedProducts[index],
      total_amount: totalAmount,
    };

    setFieldValue(
      `sales_contract_products.${index}.total_amount`,
      totalAmount || "",
    );

    calculateTotal(updatedProducts);
  };

  const handleGrossWeightChange = () => {
    if (!parentProduct) return;

    const basePackingNetWeight = isFirstOfParent
      ? n(currentProductValue.packing_net_weight)
      : n(parentProduct.packing_net_weight);

    const basePackingTypeId = isFirstOfParent
      ? currentProductValue.packing_type_id
      : parentProduct.packing_type_id;

    const packingWeight = packingType.find(
      (p: any) => p.id === basePackingTypeId,
    )?.weight;

    let packingGrossWeight = 0;
    let totalGrossWeight = 0;

    if (basePackingNetWeight > 0 && packingWeight > 0) {
      packingGrossWeight = round3(basePackingNetWeight + n(packingWeight));

      totalGrossWeight = round3(
        n(currentProductValue.no_of_bags) * packingGrossWeight,
      );
    }

    setFieldValue(
      `sales_contract_products.${index}.packing_gross_weight`,
      packingGrossWeight ?? currentProductValue.packing_gross_weight,
    );

    setFieldValue(
      `sales_contract_products.${index}.total_gross_weight`,
      totalGrossWeight || "",
    );

    setFieldValue(
      `sales_contract_products.${index}.total_net_weight`,
      n(currentProductValue.no_of_bags) * basePackingNetWeight || "",
    );

    const updatedProducts = [...values.sales_contract_products];

    updatedProducts[index] = {
      ...updatedProducts[index],
      total_gross_weight: totalGrossWeight,
      total_net_weight:
        n(currentProductValue.no_of_bags) * basePackingNetWeight,
      packing_gross_weight: packingGrossWeight,
    };

    calculateTotal(updatedProducts);
  };

  useEffect(() => {
    if (!productList.length) return;

    if (!isFirstOfParent && parentProduct) {
      const fieldsToCopy = [
        "price",
        "container_load",
        "packing_type_id",
        "packing_net_weight",
        "packing_gross_weight",
      ];

      fieldsToCopy.forEach((field) => {
        setFieldValue(
          `sales_contract_products.${index}.${field}`,
          parentProduct[field] ?? "",
          false,
        );
      });
    }
  }, [parentProduct, isFirstOfParent, productList]);

  return (
    <div
      className="row m-0 mb-3 gy-1"
      style={{
        border: "1px solid #d1d0d4",
        borderRadius: "0.375rem",
        padding: "10px 5px 20px 5px",
      }}
    >
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <div className="d-flex justify-content-between">
          <div>
            <label
              htmlFor={`sales_contract_products.${index}.product_id`}
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Product: <span className="text-danger">*</span>
            </label>
          </div>
          <div className="d-flex">
            {/* <label
              htmlFor={`sales_contract_products.${index}.is_product_variety`}
              className="form-label me-1"
              style={{ fontSize: "0.9375rem" }}
            >
              Variety
            </label>
            <div className="form-check m-0">
              <Field
                type="checkbox"
                id={`sales_contract_products.${index}.is_product_variety`}
                name={`sales_contract_products.${index}.is_product_variety`}
                checked={
                  values.sales_contract_products[index].product_variety ||
                  values.sales_contract_products[index].is_product_variety
                }
                onChange={(e: any) => {
                  handleChange(e);
                  setFieldValue(
                    `sales_contract_products.${index}.product_variety`,
                    ""
                  );
                }}
                className="form-check-input"
              />
            </div> */}
            {permissions.includes("create-product") && (
              <button
                title="Add Product"
                className="btn btn-sm btn-primary waves-effect waves-light p-0"
                type="button"
                onClick={() => setAddProductModalStatus(true)}
              >
                <span>
                  <i className="ti ti-plus my-card-icon-button"></i>
                </span>
              </button>
            )}
          </div>
        </div>
        <Field
          id={`sales_contract_products[${index}].product_id`}
          name={`sales_contract_products[${index}].product_id`}
        >
          {({ field, form }: any) => {
            const selectedOption = productList.find(
              (option: any) => option.id === field.value,
            );
            const formatOptionLabel = (option: any, { context }: any) => {
              if (context === "value") {
                return option.variety
                  ? `${option.name} - ${option.variety}`
                  : option.name;
              } else {
                return (
                  <div>
                    <label className="form-label">{option.name}</label>
                    {option.variety && " - "}
                    {option.variety && (
                      <label className="form-label">{option.variety}</label>
                    )}
                    <br />
                    <span className="form-label">{option.description}</span>
                  </div>
                );
              }
            };
            return (
              <>
                <ReactSelect
                  {...field}
                  classNamePrefix="react-select"
                  placeholder="Select Product"
                  options={productList.map((option: any) => ({
                    ...option,
                    isDisabled: values.sales_contract_products.some(
                      (p: any) => p.product_id === option.id,
                    ),
                  }))}
                  getOptionLabel={(option: any) =>
                    option.variety ? `${option.name} - ${option.variety}` : option.name
                  }
                  formatOptionLabel={formatOptionLabel}
                  getOptionValue={(option: any) => option.id}
                  onChange={async (selectedOption: any) => {
                    if (
                      productWiseUsedQty &&
                      setFieldError &&
                      productWiseUsedQty.hasOwnProperty(
                        values.sales_contract_products[index].product_id,
                      )
                    ) {
                      setFieldError(
                        `sales_contract_products[${index}].product_id`,
                        `You can not remove this product,it is used in commercial invoices`,
                      );
                      return;
                    }

                    const oldProductId =
                      values.sales_contract_products[index].product_id;
                    if (values.sales_contract_products[index]) {
                      values.sales_contract_products[index].product_id =
                        selectedOption?.id || "";
                    }
                    const updatedProductIds = values.sales_contract_products
                      .map((product: any) => product.product_id)
                      .filter(Boolean);

                    setFieldValue(
                      `sales_contract_products[${index}].product_id`,
                      selectedOption?.id || "",
                    );
                    handleChangeProduct(
                      updatedProductIds,
                      values,
                      setFieldValue,
                      oldProductId,
                      index,
                    );
                  }}
                  value={selectedOption || ""}
                  isClearable
                  isSearchable
                />
                {form.errors.sales_contract_products &&
                  form.errors.sales_contract_products[index] &&
                  form.errors.sales_contract_products[index].product_id && (
                    <div className="text-danger">
                      {form.errors.sales_contract_products[index].product_id}
                    </div>
                  )}
              </>
            );
          }}
        </Field>
      </div>

      {/* {(values.sales_contract_products[index].is_product_variety ||
        values.sales_contract_products[index].product_variety) && (
        <div className="col-12 col-sm-6 col-md-4 col-lg-3">
          <label
            htmlFor={`sales_contract_products.${index}.product_variety`}
            className="form-label"
            style={{ fontSize: "0.9375rem" }}
          >
            Variety
            <span className="text-danger">*</span>
          </label>
          <Field
            id={`sales_contract_products.${index}.product_variety`}
            name={`sales_contract_products.${index}.product_variety`}
            className="form-control"
            placeholder="Enter Product Variety"
            value={values.sales_contract_products[index].product_variety}
            onChange={handleChange}
          />
          <ErrorMessage
            name={`sales_contract_products.${index}.product_variety`}
            className="text-danger"
            component="div"
          />
        </div>
      )} */}

      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.price`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Price ({CURRENCY.USD.SYMBOL}):
          <span className="text-danger">*</span>
        </label>
        <Field
          disabled={!isFirstOfParent}
          id={`sales_contract_products.${index}.price`}
          name={`sales_contract_products.${index}.price`}
          type="number"
          className="form-control"
          placeholder="Enter Price"
          // value={values.price || undefined}
          value={currentProductValue.price || ""}
          onChange={(e: any) => {
            currentProductValue.price = updateValue(
              e.target.name,
              e.target.value,
            );

            calculateProductAmount();
            recalculateAllSiblings();
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.price`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.quantity`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Quantity (MTS): <span className="text-danger">*</span>
        </label>
        <Field
          id={`sales_contract_products.${index}.quantity`}
          name={`sales_contract_products.${index}.quantity`}
          type="number"
          className="form-control"
          placeholder="Enter Quantity"
          value={currentProductValue.quantity || ""}
          onChange={(e: any) => {
            // This run at create time
            if (
              !productWiseUsedQty ||
              (productWiseUsedQty &&
                !productWiseUsedQty[
                  values.sales_contract_products[index].product_id
                ])
            ) {
              currentProductValue.quantity = updateValue(
                e.target.name,
                e.target.value,
              );

              calculateProductAmount();
              return;
            }
            // this run at edit time
            if (productWiseUsedQty && setFieldError) {
              const productId =
                values.sales_contract_products[index].product_id;

              if (Number(e.target.value) < productWiseUsedQty[productId]) {
                setFieldError(
                  `sales_contract_products[${index}].quantity`,
                  `You can not reduce this product quantity less then ${productWiseUsedQty[productId]} as per commercial invoice's product quantity `,
                );
              } else if (
                Number(e.target.value) >= productWiseUsedQty[productId]
              ) {
                currentProductValue.quantity = updateValue(
                  e.target.name,
                  e.target.value,
                );
                calculateProductAmount();
                // handleProductCalculation(index);
              }
            }
          }}
        />

        {errors.sales_contract_products &&
          errors.sales_contract_products[index] &&
          errors.sales_contract_products[index].quantity && (
            <div className="text-danger">
              {errors.sales_contract_products[index].quantity}
            </div>
          )}
        {/* <ErrorMessage
          name={`sales_contract_products.${index}.quantity`}
          className="text-danger"
          component="div"
        /> */}
      </div>
      {/* Quantity unit always in MTS */}
      {/* <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <div className="d-flex justify-content-between">
          <div>
            <label
              htmlFor={`sales_contract_products.${index}.quantity_unit_id`}
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Quantity Unit:
            </label>
          </div>
          <div>
            <button
              title="Add Unit"
              className="btn btn-sm btn-primary waves-effect waves-light p-0"
              type="button"
              onClick={() => setAddUnitModalStatus(true)}
            >
              <span>
                <i className="ti ti-plus my-card-icon-button"></i>
              </span>
            </button>
          </div>
        </div>
        <select
          id={`sales_contract_products.${index}.quantity_unit_id`}
          name={`sales_contract_products.${index}.quantity_unit_id`}
          className="form-select"
          value={
            values.sales_contract_products[index].quantity_unit_id || undefined
          }
          onChange={handleChange}
        >
          <option defaultChecked value={""}>
            Select Quantity Unit
          </option>
          {unitList.map((unit: any) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </select>
        <ErrorMessage
          name={`sales_contract_products.${index}.quantity_unit_id`}
          className="text-danger"
          component="div"
        />
      </div> */}
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.total_amount`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Total Amount ({CURRENCY.USD.SYMBOL}):{" "}
          <span className="text-danger">*</span>
        </label>
        <Field
          disabled={!isFirstOfParent}
          id={`sales_contract_products.${index}.total_amount`}
          name={`sales_contract_products.${index}.total_amount`}
          type="number"
          className="form-control"
          placeholder="0"
          value={currentProductValue.total_amount}
          onChange={(e: any) => {
            currentProductValue.total_amount = updateValue(
              e.target.name,
              e.target.value,
            );

            const updatedProducts = [...values.sales_contract_products];

            updatedProducts[index] = {
              ...updatedProducts[index],
              total_amount: currentProductValue.total_amount,
            };

            calculateTotal(updatedProducts);
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.total_amount`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.no_of_containers`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          No. of Containers:
        </label>
        <Field
          name={`sales_contract_products.${index}.no_of_containers`}
          type="text"
          className="form-control"
          placeholder="Enter No of containers"
          onChange={handleChange}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.no_of_containers`}
          component="div"
          className="text-danger"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.container_load`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Container Size:{" "}
        </label>
        <select
          disabled={!isFirstOfParent}
          id={`sales_contract_products.${index}.container_load`}
          name={`sales_contract_products.${index}.container_load`}
          className="form-control"
          value={currentProductValue.container_load || undefined}
          onChange={handleChange}
        >
          <option defaultChecked value={""}>
            Select Container Size{""}
          </option>
          <option value={ContainerSize.TWENTY}>{ContainerSize.TWENTY}</option>
          <option value={ContainerSize.FORTY}>{ContainerSize.FORTY}</option>
        </select>
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <div className="d-flex justify-content-between">
          <div>
            <label
              htmlFor={`sales_contract_products.${index}.packing_type_id`}
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Packing Type:
            </label>
          </div>
          {permissions.includes("create-packing-type") && (
            <div>
              <button
                title="Add Packing Type"
                className="btn btn-sm btn-primary waves-effect waves-light p-0"
                type="button"
                onClick={() => setAddPackingTypeModalStatus(true)}
              >
                <span>
                  <i className="ti ti-plus my-card-icon-button"></i>
                </span>
              </button>
            </div>
          )}
        </div>
        <select
          disabled={!isFirstOfParent}
          id={`sales_contract_products.${index}.packing_type_id`}
          name={`sales_contract_products.${index}.packing_type_id`}
          className="form-select"
          value={currentProductValue?.packing_type_id}
          onChange={(e: any) => {
            setFieldValue(e.target.name, e.target.value);
            currentProductValue.packing_type_id = e.target.value;
            handleGrossWeightChange();
            recalculateAllSiblings();
          }}
        >
          <option defaultChecked value={""}>
            Select Packing Types
          </option>
          {packingType.map((packing: any) => (
            <option key={packing.id} value={packing.id}>
              {packing.name}
            </option>
          ))}
        </select>
        <ErrorMessage
          name={`sales_contract_products.${index}.packing_type_id`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.no_of_bags`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          No. of Bags:
        </label>
        <Field
          id={`sales_contract_products.${index}.no_of_bags`}
          name={`sales_contract_products.${index}.no_of_bags`}
          type="number"
          className="form-control"
          placeholder="Enter No. of bags"
          value={currentProductValue?.no_of_bags || ""}
          onChange={(e: any) => {
            currentProductValue.no_of_bags = updateValue(
              e.target.name,
              e.target.value,
            );

            const basePackingNetWeight = isFirstOfParent
              ? n(currentProductValue.packing_net_weight)
              : n(
                  values.sales_contract_products[firstIndexOfParent]
                    ?.packing_net_weight,
                );
            const basePackingGrossWeight = isFirstOfParent
              ? n(currentProductValue.packing_gross_weight)
              : n(
                  values.sales_contract_products[firstIndexOfParent]
                    ?.packing_gross_weight,
                );

            currentProductValue.total_net_weight = updateValue(
              `sales_contract_products.${index}.total_net_weight`,
              n(e.target.value) * basePackingNetWeight,
            );

            currentProductValue.total_gross_weight = updateValue(
              `sales_contract_products.${index}.total_gross_weight`,
              round3(n(e.target.value) * basePackingGrossWeight),
            );

            const updatedProducts = [...values.sales_contract_products];

            updatedProducts[index] = {
              ...updatedProducts[index],
              total_net_weight: currentProductValue.total_net_weight,
              total_gross_weight: currentProductValue.total_gross_weight,
            };

            calculateTotal(updatedProducts);
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.no_of_bags`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.packing_net_weight`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Packing Net Weight (KGS):
        </label>
        <Field
          disabled={!isFirstOfParent}
          id={`sales_contract_products.${index}.packing_net_weight`}
          name={`sales_contract_products.${index}.packing_net_weight`}
          type="number"
          className="form-control"
          placeholder="Enter Packing Net Weight"
          value={currentProductValue.packing_net_weight || ""}
          onChange={(e: any) => {
            currentProductValue.packing_net_weight = updateValue(
              e.target.name,
              e.target.value,
            );
            currentProductValue.total_net_weight = updateValue(
              `sales_contract_products.${index}.total_net_weight`,
              n(currentProductValue.no_of_bags) *
                n(currentProductValue.packing_net_weight),
            );
            handleGrossWeightChange();
            recalculateAllSiblings();
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.packing_net_weight`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label className="form-label" style={{ fontSize: "0.9375rem" }}>
          Total Packing Net Weight (KGS):
        </label>
        <Field
          disabled={!isFirstOfParent}
          type="number"
          id={`sales_contract_products.${index}.total_net_weight`}
          name={`sales_contract_products.${index}.total_net_weight`}
          className="form-control"
          value={currentProductValue?.total_net_weight}
          onChange={(e: any) => {
            currentProductValue.total_net_weight = updateValue(
              e.target.name,
              e.target.value,
            );
            const updatedProducts = [...values.sales_contract_products];

            updatedProducts[index] = {
              ...updatedProducts[index],
              total_net_weight: currentProductValue.total_net_weight,
            };

            calculateTotal(updatedProducts);
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.total_net_weight`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.packing_gross_weight`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Packing Gross Weight (KGS):
        </label>
        <Field
          disabled={!isFirstOfParent}
          id={`sales_contract_products.${index}.packing_gross_weight`}
          name={`sales_contract_products.${index}.packing_gross_weight`}
          type="number"
          className="form-control"
          placeholder="Enter Packing Gross Weight"
          value={currentProductValue?.packing_gross_weight || ""}
          onChange={(e: any) => {
            currentProductValue.packing_gross_weight = updateValue(
              e.target.name,
              e.target.value,
            );
            currentProductValue.total_gross_weight = updateValue(
              `sales_contract_products.${index}.total_gross_weight`,
              n(currentProductValue.no_of_bags) * n(e.target.value),
            );
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.packing_gross_weight`}
          className="text-danger"
          component="div"
        />
      </div>
      <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label className="form-label" style={{ fontSize: "0.9375rem" }}>
          Total Packing Gross Weight (KGS):
        </label>
        <Field
          disabled={!isFirstOfParent}
          type="number"
          id={`sales_contract_products.${index}.total_gross_weight`}
          name={`sales_contract_products.${index}.total_gross_weight`}
          className="form-control"
          value={currentProductValue?.total_gross_weight}
          onChange={(e: any) => {
            currentProductValue.total_gross_weight = updateValue(
              e.target.name,
              e.target.value,
            );
            const updatedProducts = [...values.sales_contract_products];

            updatedProducts[index] = {
              ...updatedProducts[index],
              total_gross_weight: currentProductValue.total_gross_weight,
            };

            calculateTotal(updatedProducts);
          }}
        />
        <ErrorMessage
          name={`sales_contract_products.${index}.total_gross_weight`}
          className="text-danger"
          component="div"
        />
      </div>
      {/* Packing unit always in KGS */}
      {/* <div className="col-12 col-sm-6 col-md-4 col-lg-3">
        <label
          htmlFor={`sales_contract_products.${index}.packing_unit_id`}
          className="form-label"
          style={{ fontSize: "0.9375rem" }}
        >
          Packing Unit:
        </label>
        <select
          id={`sales_contract_products.${index}.packing_unit_id`}
          name={`sales_contract_products.${index}.packing_unit_id`}
          className="form-select"
          value={
            values.sales_contract_products[index].packing_unit_id || undefined
          }
          onChange={handleChange}
        >
          <option defaultChecked value={""}>
            Select Packing Unit
          </option>
          {unitList.map((unit: any) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
        </select>
        <ErrorMessage
          name={`sales_contract_products.${index}.packing_unit_id`}
          className="text-danger"
          component="div"
        />
      </div> */}

      <div className="col-12 col-sm-6 col-lg-2 d-flex align-items-end">
        <button
          type="button"
          className="btn btn-danger mt-3"
          onClick={() => {
            const deletedProduct_id =
              values.sales_contract_products[index]?.product_id;

            if (
              productWiseUsedQty &&
              setFieldError &&
              productWiseUsedQty[
                values.sales_contract_products[index]?.product_id
              ]
            ) {
              setFieldError(
                `sales_contract_products[${index}].product_id`,
                `You can not remove this product's data,it is used in commercial invoices`,
              );
              return;
            }

            const updatedProductIds = values.sales_contract_products
              .filter(
                (product: any) => product.product_id !== deletedProduct_id,
              )
              .map((item: any) => item.product_id)
              .filter(Boolean);

            handleChangeProduct(
              updatedProductIds,
              values,
              setFieldValue,
              deletedProduct_id,
              index,
            );

            remove(index);

            const newSaleItems = values.sales_contract_products.filter(
              (_: any, indexKey: number) => indexKey !== index,
            );
            values.sales_contract_products = newSaleItems;
            calculateTotal(newSaleItems);
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default ProductFields;
